// Carry Roll Flight integration — PSD §4.2.3. Velocity-Verlet 3D integrator + Basic reduced model (4.5).
import { BALL_AREA, BALL_MASS, DT_FLIGHT, G, MAX_T } from './constants';
import { dragCoeff, liftCoeff, spinDecay } from './aerodynamics';
import { airDensity } from './environment';
import { applyBallModel } from './ballModel';
import { degToRad, radToDeg } from './units';
import { Fidelity } from './types';
/**
 * Simulate ball flight until the ball first crosses the ground plane (y <= 0).
 * Full fidelity: velocity-Verlet integration in 3D with drag, Magnus lift, gravity.
 * Basic fidelity: closed-form reduced projectile model with lumped drag coefficient (4.5).
 */
export function simulateFlight(req) {
    if (req.fidelity === Fidelity.Basic) {
        return reducedFlightModel(req);
    }
    return fullFlightModel(req);
}
/**
 * Basic fidelity closed-form reduced model (4.5).
 * Projectile with lumped drag coefficient — fast estimate (~0.2 ms/shot).
 * Internally consistent with Full but simplified aero.
 */
function reducedFlightModel(req) {
    const warnings = [];
    const launch = req.launch;
    const env = req.environment;
    const { rho, warnings: envWarnings } = airDensity(env);
    warnings.push(...envWarnings);
    const bm = applyBallModel(req.ballType);
    const v = launch.ballSpeed * bm.speedFactor;
    let th = launch.launchAngle;
    if (th <= 0)
        th = degToRad(1);
    // Lumped drag coefficient — simplified constant Cd
    const Cd = 0.28 * bm.dragAdjust;
    const k = (0.5 * rho * Cd * BALL_AREA) / BALL_MASS;
    // Analytic approximation: iterative analytic with linearized drag
    // Use a few iterations to converge on carry distance
    const sinT = Math.sin(th);
    const cosT = Math.cos(th);
    const range = (v * v * sinT * cosT) / G;
    // Drag correction factor (empirical lumped model)
    const dragFactor = 1 / (1 + k * v * range * 0.5);
    const carryM = range * dragFactor;
    const apexM = (v * v * sinT * sinT) / (2 * G) * dragFactor;
    // Approximate flight time
    const tFlight = (2 * v * sinT) / G;
    // Landing angle approximation
    const vxLand = v * cosT * dragFactor;
    const vyLand = G * (tFlight / 2) * dragFactor;
    const landingAngleDeg = radToDeg(Math.atan2(vyLand, Math.max(vxLand, 1e-9)));
    // Minimal flight path (start, apex, landing)
    const az = degToRad(launch.launchDirDeg);
    const flightPath = [
        { t: 0, x: 0, y: 0, z: 0, vx: v * cosT * Math.cos(az), vy: v * sinT, vz: v * cosT * Math.sin(az) },
        {
            t: tFlight / 2,
            x: carryM / 2,
            y: apexM,
            z: (carryM / 2) * Math.tan(az),
            vx: vxLand,
            vy: 0,
            vz: vxLand * Math.tan(az),
        },
        {
            t: tFlight,
            x: carryM,
            y: 0,
            z: carryM * Math.tan(az),
            vx: vxLand,
            vy: vyLand,
            vz: vxLand * Math.tan(az),
        },
    ];
    warnings.push({
        field: 'fidelity',
        code: 'info',
        message: 'Basic fidelity reduced model — results are approximate.',
    });
    return {
        flightPath,
        carryM: Math.max(carryM, 0),
        apexM,
        landingAngleDeg,
        vxLand,
        zLand: carryM * Math.tan(az),
        warnings,
    };
}
/**
 * Full fidelity velocity-Verlet 3D integrator.
 */
function fullFlightModel(req) {
    const warnings = [];
    const launch = req.launch;
    const env = req.environment;
    const { rho, warnings: envWarnings } = airDensity(env);
    warnings.push(...envWarnings);
    // Launch angle guard
    let th = launch.launchAngle;
    if (th <= 0) {
        th = Math.max(th, degToRad(1));
        warnings.push({
            field: 'launch.launchAngle',
            code: 'range',
            message: 'Launch angle too low; clamped upward to produce flight.',
        });
    }
    // Ball-type modifiers (6.7)
    const bm = applyBallModel(req.ballType);
    const v = launch.ballSpeed * bm.speedFactor;
    const az = degToRad(launch.launchDirDeg);
    let vx = v * Math.cos(th) * Math.cos(az);
    let vy = v * Math.sin(th);
    let vz = v * Math.cos(th) * Math.sin(az);
    let x = 0;
    let y = 0;
    let z = 0;
    let spin = launch.spinRpm * bm.spinRetention;
    let t = 0;
    let dt = DT_FLIGHT;
    const path = [];
    // Ground plane offset: target elevation (5.9). Uphill (positive diff) shortens carry.
    const groundPlane = -env.elevationDiffM;
    let prevSpeed = v;
    const spinAxisRad = degToRad(launch.spinAxisDeg);
    const cosAxis = Math.cos(spinAxisRad); // backspin component (vertical lift)
    const sinAxis = Math.sin(spinAxisRad); // sidespin component (lateral)
    path.push({ t, x, y, z, vx, vy, vz });
    while (t < MAX_T) {
        const speed = Math.hypot(vx, vy, vz);
        if (speed < 1) {
            // below singularity threshold — negligible aero
            vx += 0;
            vy += -G * dt;
            vz += 0;
        }
        else {
            const Cd = dragCoeff(speed) * bm.dragAdjust;
            const Cl = liftCoeff(spin / 60, speed) * bm.liftAdjust;
            // Wind relative velocity (windDirDeg: 0 = headwind → opposes x)
            const wx = -env.windSpeed * Math.cos(degToRad(env.windDirDeg));
            const wz = -env.windSpeed * Math.sin(degToRad(env.windDirDeg));
            const rvx = vx - wx;
            const rvy = vy; // no vertical wind
            const rvz = vz - wz;
            const rspeed = Math.hypot(rvx, rvy, rvz);
            const dragMag = 0.5 * rho * Cd * BALL_AREA * rspeed;
            const liftMag = 0.5 * rho * Cl * BALL_AREA * rspeed;
            // Drag opposes relative motion
            const invR = rspeed > 1e-9 ? 1 / rspeed : 0;
            const adx = -dragMag * rvx * invR;
            const ady = -dragMag * rvy * invR;
            const adz = -dragMag * rvz * invR;
            // Magnus lift: dominant vertical (cosAxis) + lateral (sinAxis).
            // Lift perpendicular to velocity, in the plane normal to spin axis.
            const liftDirX = cosAxis * (-rvy) * invR;
            const liftDirY = cosAxis * Math.hypot(rvx, rvz) * invR;
            const liftDirZ = sinAxis * rspeed * invR;
            const alx = liftMag * liftDirX;
            const aly = liftMag * liftDirY;
            const alz = liftMag * liftDirZ;
            const ax = (adx + alx) / BALL_MASS;
            const ay = (ady + aly) / BALL_MASS - G;
            const az_a = (adz + alz) / BALL_MASS;
            vx += ax * dt;
            vy += ay * dt;
            vz += az_a * dt;
        }
        x += vx * dt;
        y += vy * dt;
        z += vz * dt;
        t += dt;
        spin = spinDecay(spin, dt);
        // Adaptive dt: halve if per-step speed change > 5%
        const speedChange = Math.abs(Math.hypot(vx, vy, vz) - prevSpeed);
        if (speed > 1e-9 && speedChange / prevSpeed > 0.05 && dt > 1e-4) {
            dt = dt / 2;
        }
        prevSpeed = Math.hypot(vx, vy, vz);
        if (y <= groundPlane) {
            // Ground crossing — record then interpolate the crossing point.
            path.push({ t, x, y, z, vx, vy, vz });
            break;
        }
        path.push({ t, x, y, z, vx, vy, vz });
    }
    // Interpolate ground crossing for accurate carry/apex.
    const n = path.length;
    let carryX = path[n - 1].x;
    let landPoint = path[n - 1];
    if (n >= 2) {
        const p1 = path[n - 2];
        const p2 = path[n - 1];
        if (p2.y <= groundPlane && p1.y > groundPlane) {
            const frac = (p1.y - groundPlane) / (p1.y - p2.y);
            carryX = p1.x + (p2.x - p1.x) * frac;
            landPoint = {
                t: p1.t + (p2.t - p1.t) * frac,
                x: carryX,
                y: groundPlane,
                z: p1.z + (p2.z - p1.z) * frac,
                vx: p1.vx + (p2.vx - p1.vx) * frac,
                vy: p1.vy + (p2.vy - p1.vy) * frac,
                vz: p1.vz + (p2.vz - p1.vz) * frac,
            };
        }
    }
    const apex = path.reduce((m, p) => (p.y > m ? p.y : m), 0);
    const landingAngleDeg = radToDeg(Math.atan2(-landPoint.vy, Math.max(landPoint.vx, 1e-9)));
    return {
        flightPath: path,
        carryM: Math.max(carryX, 0),
        apexM: apex,
        landingAngleDeg,
        vxLand: landPoint.vx,
        zLand: landPoint.z,
        warnings,
    };
}
//# sourceMappingURL=flight.js.map